import { useMemo, type ReactNode } from "react";
import { api } from "../../convex-api/api";
import { DASHBOARD_SYMBOLS } from "../../lib/dashboardDataContract";
import { buildFbeCardGuidance, formatFbeSetupList, type FbeForecastFields } from "../../lib/fbeForecastView";
import { useQueryWithState, type QueryState } from "../../hooks/useQueryWithState";
import { EmptyState, ErrorState, LoadingState } from "../states";
import "../markov/MarkovWorkspace.css";

type FbeBiasSessionFilter = "asia" | "us" | "all";
type FbeBiasSessionKey = "asia" | "us";

type FbeBiasSymbolRow = FbeForecastFields & Record<string, unknown> & { symbol: string; created_at?: number | null };

type FbeBiasSnapshot = {
  session?: string;
  date?: string | null;
  created_at?: number | null;
  source?: string;
  symbols?: Record<string, Record<string, unknown>>;
};

type FbeBiasStates = Record<FbeBiasSessionKey, QueryState<FbeBiasSnapshot>>;

interface FbeBiasWorkspaceProps {
  sessionFilter: FbeBiasSessionFilter;
}

interface FbeBiasWorkspaceInnerProps extends FbeBiasWorkspaceProps {
  states: FbeBiasStates;
}

const FBE_BIAS_SYMBOL_ORDER = DASHBOARD_SYMBOLS;

/**
 * FbeBiasWorkspace is the trader-POV companion to Markov Regime.
 *
 * It renders the live FBE session-end bias as setup guidance only. It does not
 * import accountability/outcome rows, mutate forecast fields, approve trading,
 * or participate in alerts, signal gates, sizing, or execution.
 */
export function FbeBiasWorkspace({ sessionFilter }: FbeBiasWorkspaceProps) {
  const asiaState = useQueryWithState(api.queries.getLatestIntelBySymbol, { session: "asia" }) as QueryState<FbeBiasSnapshot>;
  const usState = useQueryWithState(api.queries.getLatestIntelBySymbol, { session: "us" }) as QueryState<FbeBiasSnapshot>;

  return <FbeBiasWorkspaceInner sessionFilter={sessionFilter} states={{ asia: asiaState, us: usState }} />;
}

export function FbeBiasWorkspaceInner({ sessionFilter, states }: FbeBiasWorkspaceInnerProps) {
  const currentFilter = sessionFilter === "all" ? "All sessions" : sessionFilter === "asia" ? "Asia session" : "US session";
  const sessions = filteredSessions(sessionFilter);

  return (
    <main className="markov-view" aria-label="FBE bias workspace">
      <section className="markov-hero fbe-bias-hero">
        <div>
          <span className="markov-kicker">LOG-ONLY · session forecast · not execution</span>
          <h2>FBE Session Forecast</h2>
          <p>Anchored session-open FBE forecast translated into watch-only market context: bullish, bearish, ranging, or no-edge. ORB/FBD/MR/order-flow still decide entries and risk.</p>
        </div>
        <div className="markov-status">{currentFilter}</div>
      </section>

      <section className="markov-workspace" aria-label="FBE bias trader guidance workspace">
        <div className="markov-workspace-header">
          <h3>Session FBE forecasts</h3>
          <div className="markov-meta-row">
            <span>Source: liveMarketSnapshots.payload.forecast_*</span>
            <span>Contract: bias_not_signal</span>
            <span>{sessionFilter === "all" ? "Asia and US queried independently" : "Selected session queried directly"}</span>
          </div>
        </div>
        <div className="markov-session-grid">
          {sessions.map((item) => (
            <section key={item.key} className="markov-session-panel active" aria-label={`FBE ${item.label} bias guidance`}>
              <h3>{item.label} session</h3>
              <SessionMeta state={states[item.key]} />
              <p className="markov-panel-note">Read-only context for which setup family has forecast support into session close. It is not a trade signal.</p>
              <QueryStateBlock state={states[item.key]}>
                {(data) => <FbeBiasSymbolGrid snapshot={data} />}
              </QueryStateBlock>
            </section>
          ))}
        </div>
      </section>
    </main>
  );
}

function QueryStateBlock({ state, children }: { state: QueryState<FbeBiasSnapshot>; children: (data: FbeBiasSnapshot) => ReactNode }) {
  if (state.status === "loading") return <LoadingState label="FBE bias" block />;
  if (state.status === "error") return <ErrorState error={state.error} onRetry={state.retry} label="FBE bias" block />;
  if (!state.data.symbols || Object.keys(state.data.symbols).length === 0) {
    return <EmptyState message="No live FBE bias snapshot is available yet." block />;
  }
  return <>{children(state.data)}</>;
}

function FbeBiasSymbolGrid({ snapshot }: { snapshot: FbeBiasSnapshot }) {
  const rows = useMemo(() => orderedSymbols(snapshot.symbols ?? {}), [snapshot.symbols]);
  return (
    <div className="markov-symbol-grid">
      {rows.map((row) => {
        const guidance = buildFbeCardGuidance(row);
        return (
          <article key={String(row.symbol)} className="markov-symbol-card fbe-bias-symbol-card" data-fbe-state={guidance.bias}>
            <div className="markov-symbol-head">
              <strong>{String(row.symbol)}</strong>
              <span>{formatFreshness(asNumber(row.created_at))}</span>
            </div>
            <div className={`fbe-bias-primary fbe-bias-${guidance.bias}`}>
              {guidance.primaryText}
            </div>
            <div className="markov-state-list">
              <InfoLine label="Mode" value={guidance.tradeMode?.replace(/_/g, " ") ?? "—"} />
              <InfoLine label="Confidence" value={`${guidance.confidenceText} · ${guidance.reliabilityText}`} />
              <InfoLine label="Setup context" value={guidance.preferredSetups.length ? formatFbeSetupList(guidance.preferredSetups) : "—"} />
              <InfoLine label="Weak setup context" value={guidance.avoidSetups.length ? formatFbeSetupList(guidance.avoidSetups) : "—"} />
              <InfoLine label="Contract" value={guidance.guidanceContract ?? "bias_not_signal"} />
              <InfoLine label="Data" value={guidance.dataLine} />
              <InfoLine label="Calibration" value={guidance.calibrationLine} />
              <InfoLine label="Governance" value={guidance.governanceLine} />
            </div>
            {guidance.guidanceReason ? <div className="markov-combined-read">{guidance.guidanceReason}</div> : null}
            <div className="markov-source-line">{guidance.statusText} · execution disabled</div>
          </article>
        );
      })}
    </div>
  );
}

function InfoLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="markov-state-line fbe-bias-info-line">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SessionMeta({ state }: { state: QueryState<FbeBiasSnapshot> }) {
  const snapshot = state.status === "success" ? state.data : null;
  return (
    <div className="markov-meta-row" aria-label="FBE bias session metadata">
      <span>Source: {snapshot?.source ?? "liveMarketSnapshots"}</span>
      <span>Session: {snapshot?.session ?? "loading"}</span>
      <span>Freshness: {formatFreshness(snapshot?.created_at ?? undefined)}</span>
    </div>
  );
}

function orderedSymbols(symbols: Record<string, Record<string, unknown>>): FbeBiasSymbolRow[] {
  const rows: FbeBiasSymbolRow[] = [];
  for (const symbol of FBE_BIAS_SYMBOL_ORDER) {
    const row = symbols[symbol];
    if (row) rows.push({ ...row, symbol });
  }
  return rows;
}

function filteredSessions(sessionFilter: FbeBiasSessionFilter): readonly { key: FbeBiasSessionKey; label: string }[] {
  const sessions = [
    { key: "asia", label: "Asia" },
    { key: "us", label: "US" },
  ] as const;

  if (sessionFilter === "all") return sessions;
  return sessions.filter((item) => item.key === sessionFilter);
}

function asNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function formatFreshness(createdAt?: number | null) {
  if (!createdAt) return "unknown";
  const ageMs = Date.now() - createdAt;
  if (!Number.isFinite(ageMs) || ageMs < 0) return "fresh";
  const ageMin = Math.round(ageMs / 60000);
  return ageMin < 1 ? "<1m" : `${ageMin}m`;
}
